const DESKTOP_QUERY = '(min-width: 1280px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const DRAWER_EXIT_MS = 240;
const TITLE_SWAP_MS = 300;
const SCROLL_LOCK_CLASS = 'header-nav-scroll-locked';
const LEVEL_EXIT_MS = 320;

class HeaderNavigation extends HTMLElement {
  connectedCallback() {
    const root = this.querySelector('dialog');
    this.root = root;
    this.desktop = window.matchMedia(DESKTOP_QUERY);
    this.shellLabel = root.getAttribute('aria-label') || 'Main navigation';
    this.reduce = window.matchMedia(REDUCED_MOTION_QUERY);

    this.stack = [];
    this.exits = new Map();
    this.ordered = new Map();
    this.opener = null;
    this.drawerExit = null;
    this.titleSwap = null;
    this.swapTimer = null;
    this.deselectFrame = null;

    this.backButton = root.querySelector('[data-header-nav-back]');
    this.closeButton = root.querySelector('[data-header-nav-close]');
    this.backLabel = root.querySelector('[data-header-nav-back-label]');
    this.titleNode = root.querySelector('[data-header-nav-title]');
    this.rootLevel = root.querySelector('[data-header-nav-list]');
    this.levelsHost = root.querySelector('[data-header-nav-levels]');

    this.abort = new AbortController();
    const signal = this.abort.signal;
    const on = (target, type, handler, options) =>
      target.addEventListener(type, (event) => handler.call(this, event), { ...options, signal });

    this.panels().forEach((panel) => {
      on(panel, 'beforetoggle', this.onPanelToggle);
      on(panel, 'toggle', this.onPanelSettled);
    });
    on(root, 'click', this.onLocalizationClick, { capture: true });
    on(root, 'click', this.onRootClick);
    on(root, 'pointerdown', this.onRootPointerDown);
    on(root, 'cancel', this.onDrawerCancel);
    on(root, 'close', this.onDrawerClose);
    on(this, 'shopify:block:select', this.onBlockSelect);
    on(this, 'shopify:block:deselect', this.onBlockDeselect);
    on(document, 'click', this.onOpenerClick);
    on(document, 'keydown', this.onKeydown);
    on(document, 'focusin', this.onDocumentFocusIn);
    on(window, 'resize', this.onViewportChange);
    on(window, 'scroll', this.onViewportChange, { passive: true });
    on(this.desktop, 'change', this.onBreakpointChange);

    this.trackLayout();
    this.setShellSemantics(this.isDrawerOpen);
  }

  disconnectedCallback() {
    this.endSwap();
    this.cancelTitleSwap();
    window.cancelAnimationFrame(this.deselectFrame);
    this.cancelDrawerExit();
    this.closeAll();
    this.closeDrawer({ restoreFocus: false, immediate: true });
    this.releaseDrawer();
    this.abort.abort();
  }

  setShellSemantics(isDialog) {
    if (isDialog) {
      this.root.removeAttribute('role');
      this.root.setAttribute('aria-label', this.shellLabel);
      return;
    }

    this.root.setAttribute('role', 'presentation');
    this.root.removeAttribute('aria-label');
  }

  trackLayout() {
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const width = Math.round(entry.contentRect.width);
        if (!width) return;
        if (entry.target.tagName === 'IMG') {
          if (entry.target.sizes !== width + 'px') entry.target.sizes = width + 'px';
        } else if (this.isDesktop) {
          const column = entry.target;
          const panel = column.closest('[data-header-nav-panel]');
          panel.style.setProperty('--nav-feature-left', column.offsetLeft + 'px');
          panel.style.setProperty('--nav-feature-right', panel.clientWidth - column.offsetLeft - column.offsetWidth + 'px');
        }
      });
    });

    this.root.querySelectorAll('img[sizes^="auto"], .header-nav__panels > .header-nav-panel:last-of-type')
      .forEach((node) => observer.observe(node));
    this.abort.signal.addEventListener('abort', () => observer.disconnect());
  }

  get isDesktop() {
    return this.desktop.matches;
  }

  panels() {
    return this.root.querySelectorAll('[data-header-nav-panel]');
  }

  get openPanel() {
    return this.root.querySelector('[data-header-nav-panel]:popover-open');
  }

  itemFor(element) {
    return element.closest('[data-header-nav-item]');
  }

  onPanelToggle(event) {
    const panel = event.target;
    const item = this.itemFor(panel);

    if (event.newState === 'open') {
      if (!this.isDesktop) {
        event.preventDefault();
        return;
      }
      this.measureOffset();
      this.markSwap(panel);
      if (item) {
        item.setAttribute('data-nav-open', '');
        this.setExpanded(item, true);
        this.collapseFeatures(item);
      }
      return;
    }

    if (item) {
      item.removeAttribute('data-nav-open');
      this.setExpanded(item, false);
      this.collapseFeatures(item);
    }
  }

  onPanelSettled() {
    this.lockScroll(this.isDrawerOpen || Boolean(this.openPanel));
  }

  lockScroll(locked) {
    document.documentElement.classList.toggle(SCROLL_LOCK_CLASS, locked);
  }

  onRootPointerDown(event) {
    if (!this.isDesktop) return;

    const trigger = event.target.closest('[data-header-nav-trigger]');
    if (!trigger) return;

    const open = this.openPanel;
    if (!open) return;

    const item = this.itemFor(trigger);
    const panel = item && item.querySelector('[data-header-nav-panel]');
    if (panel && panel !== open) this.beginSwap();
  }

  markSwap(opening) {
    const open = this.openPanel;
    if (open && open !== opening) this.beginSwap();
    if (this.root.hasAttribute('data-nav-swap')) {
      window.clearTimeout(this.swapTimer);
      this.swapTimer = window.setTimeout(() => this.endSwap(), 0);
    }
  }

  beginSwap() {
    this.root.setAttribute('data-nav-swap', '');
    window.clearTimeout(this.swapTimer);
    this.swapTimer = window.setTimeout(() => this.endSwap(), 400);
  }

  endSwap() {
    window.clearTimeout(this.swapTimer);
    this.swapTimer = null;
    this.root.removeAttribute('data-nav-swap');
  }

  setExpanded(item, open) {
    const trigger = item.querySelector('[data-header-nav-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  onRootClick(event) {
    this.flushExits();

    if (event.target.closest('[data-header-nav-back]')) {
      event.preventDefault();
      this.pop();
      return;
    }

    if (event.target.closest('[data-header-nav-close]')) {
      event.preventDefault();
      this.closeDrawer();
      return;
    }

    const selector = event.target.closest('[data-header-nav-feature-selector]');
    if (selector && !this.isDesktop) {
      event.preventDefault();
      const feature = selector.closest('[data-nav-feature]');
      const panel = feature && feature.querySelector('[data-header-nav-feature-panel]');
      if (panel) this.push(panel, selector);
      return;
    }

    const trigger = event.target.closest('[data-header-nav-trigger]');
    if (trigger && !this.isDesktop) {
      event.preventDefault();
      const item = this.itemFor(trigger);
      const panel = item && item.querySelector('[data-header-nav-panel]');
      if (panel) this.push(panel, trigger);
    }
  }

  onLocalizationClick(event) {
    if (this.isDesktop || !this.isDrawerOpen) return;
    if (!event.target.closest('[data-header-nav-localization]')) return;

    this.closeDrawer({ restoreFocus: false, immediate: true });
  }

  onKeydown(event) {
    if (event.key !== 'Escape' || event.defaultPrevented || !this.isDesktop) return;

    const panel = this.openPanel;
    if (!panel) return;

    event.preventDefault();
    const item = this.itemFor(panel);
    panel.hidePopover();

    const trigger = item && item.querySelector('[data-header-nav-trigger]');
    if (trigger) trigger.focus();
  }

  onOpenerClick(event) {
    const opener = event.target.closest('[data-header-nav-opener]');
    if (!opener || this.isDesktop) return;

    event.preventDefault();
    this.opener = opener;
    this.openDrawer();
  }

  onDrawerCancel(event) {
    event.preventDefault();
    this.closeDrawer();
  }

  onDrawerClose() {
    this.releaseDrawer();
  }

  get isDrawerOpen() {
    return this.root.matches(':modal');
  }

  openDrawer() {
    const reopening = this.cancelDrawerExit();
    if (!reopening && this.isDrawerOpen) return;

    if (!this.isDrawerOpen) {
      this.setShellSemantics(true);
      this.root.removeAttribute('open');
      this.root.showModal();
    }
    this.lockScroll(true);
    this.resetStack();
    if (this.closeButton) this.closeButton.focus();
  }

  closeDrawer(options) {
    const restoreFocus = !options || options.restoreFocus !== false;
    if (!this.isDrawerOpen) return;

    const opener = this.opener;
    const finish = () => {
      this.cancelDrawerExit();
      this.root.close();
      this.releaseDrawer();
      if (!this.isDesktop) this.root.setAttribute('open', '');
      if (restoreFocus && opener && opener.isConnected) opener.focus();
    };

    if ((options && options.immediate) || this.isDesktop || this.reduce.matches) {
      finish();
      return;
    }

    this.cancelDrawerExit();
    this.root.setAttribute('data-nav-closing', '');
    this.drawerExit = window.setTimeout(finish, DRAWER_EXIT_MS);
  }

  cancelDrawerExit() {
    this.root.removeAttribute('data-nav-closing');
    if (!this.drawerExit) return false;

    window.clearTimeout(this.drawerExit);
    this.drawerExit = null;
    return true;
  }

  releaseDrawer() {
    this.setShellSemantics(false);
    this.resetStack();
    this.root.querySelectorAll('[inert]').forEach((node) => node.removeAttribute('inert'));
    this.lockScroll(false);
  }

  setTitle(text, motion) {
    if (!this.titleNode) return;

    this.cancelTitleSwap();
    this.root.removeAttribute('data-nav-title-swap');

    if (!motion || this.reduce.matches) {
      this.titleNode.textContent = text;
      return;
    }

    this.titleNode.getBoundingClientRect();
    this.root.setAttribute('data-nav-title-swap', motion);

    if (motion === 'in') {
      this.titleNode.textContent = text;
      return;
    }

    this.titleSwap = window.setTimeout(() => {
      this.titleNode.textContent = text;
      this.titleSwap = null;
    }, TITLE_SWAP_MS / 2);
  }

  titleMotion(previous, depth) {
    if (previous === depth) return null;
    if (previous > 0 && depth > 0) return 'swap';
    return depth === 0 ? 'out' : 'in';
  }

  cancelTitleSwap() {
    if (!this.titleSwap) return;

    window.clearTimeout(this.titleSwap);
    this.titleSwap = null;
  }

  currentLevel() {
    const current = this.stack[this.stack.length - 1];
    return current ? current.node : this.rootLevel;
  }

  push(node, opener) {
    const from = this.currentLevel();
    if (!from || node === from) return;

    const pending = this.cancelExit(node);

    this.stack.push({
      node: node,
      opener: opener || null,
      scrollTop: from.scrollTop,
      parent: pending ? pending.parent : node.parentElement,
      next: pending ? pending.next : node.nextSibling
    });
    if (opener) opener.setAttribute('aria-expanded', 'true');

    this.orderSections(node);
    this.levelsHost.appendChild(node);
    this.setLevelState(from, false, true);
    node.getBoundingClientRect();
    this.setLevelState(node, true);
    this.syncChrome();

    node.scrollTop = 0;
    if (this.titleNode) this.titleNode.focus();
  }

  restore(entry, immediate) {
    if (entry.opener) {
      if (entry.opener.matches('summary')) entry.opener.removeAttribute('aria-expanded');
      else entry.opener.setAttribute('aria-expanded', 'false');
    }
    this.setLevelState(entry.node, false, false);
    this.cancelExit(entry.node);

    if (immediate) {
      this.reparent(entry);
      return;
    }

    entry.timer = window.setTimeout(() => this.reparent(entry), LEVEL_EXIT_MS);
    this.exits.set(entry.node, entry);
  }

  orderSections(panel) {
    const host = panel.querySelector('.header-nav__panels');
    if (!host || this.ordered.has(host)) return;
    // A column heading groups its children; flattening would split them.
    if (host.querySelector('.header-nav-panel__heading')) return;

    const sections = Array.from(
      host.querySelectorAll(':scope > * > [data-nav-content], :scope > * > [data-header-nav-promo]')
    ).map((node) => ({ node: node, parent: node.parentElement }));
    if (!sections.length) return;

    const fragment = document.createDocumentFragment();
    sections.forEach((entry) => fragment.appendChild(entry.node));
    host.appendChild(fragment);

    panel.setAttribute('data-nav-ordered', '');
    this.ordered.set(host, sections);
  }

  restoreSections(panel) {
    const host = panel && panel.querySelector('.header-nav__panels');
    const sections = host && this.ordered.get(host);
    if (!sections) return;

    const fragments = new Map();
    sections.forEach((entry) => {
      if (!this.root.contains(entry.parent)) return;
      let fragment = fragments.get(entry.parent);
      if (!fragment) {
        fragment = document.createDocumentFragment();
        fragments.set(entry.parent, fragment);
      }
      fragment.appendChild(entry.node);
    });
    fragments.forEach((fragment, parent) => parent.appendChild(fragment));
    panel.removeAttribute('data-nav-ordered');
    this.ordered.delete(host);
  }

  cancelExit(node) {
    const entry = this.exits.get(node);
    if (!entry) return null;

    window.clearTimeout(entry.timer);
    this.exits.delete(node);
    return entry;
  }

  reparent(entry) {
    this.cancelExit(entry.node);
    if (entry.node.hasAttribute('data-nav-current')) return;
    this.restoreSections(entry.node);
    if (this.root.contains(entry.parent)) {
      entry.parent.insertBefore(entry.node, entry.next);
    }
  }

  flushExits() {
    Array.from(this.exits.values()).forEach((entry) => this.reparent(entry));
  }

  flushSections() {
    Array.from(this.ordered.keys()).forEach((host) => {
      const panel = host.closest('[data-header-nav-panel]') || host.parentElement;
      this.restoreSections(panel);
    });
  }

  pop() {
    const entry = this.stack.pop();
    if (!entry) return;

    this.restore(entry);
    const to = this.currentLevel();
    if (to) {
      this.setLevelState(to, true);
      to.scrollTop = entry.scrollTop;
    }
    this.syncChrome();

    if (entry.opener && entry.opener.isConnected) {
      entry.opener.focus();
    } else if (this.titleNode) {
      this.titleNode.focus();
    }
  }

  resetStack() {
    this.flushExits();
    this.flushSections();
    this.stack.slice().reverse().forEach((entry) => this.restore(entry, true));
    this.stack = [];
    if (this.rootLevel) {
      this.setLevelState(this.rootLevel, true);
      this.rootLevel.scrollTop = 0;
    }
    this.syncChrome();
  }

  setLevelState(node, isCurrent, behind) {
    if (!node) return;

    if (isCurrent) {
      node.setAttribute('data-nav-current', '');
      node.removeAttribute('data-nav-behind');
      node.removeAttribute('inert');
      return;
    }

    node.removeAttribute('data-nav-current');
    node.setAttribute('inert', '');

    if (behind) {
      node.setAttribute('data-nav-behind', '');
    } else {
      node.removeAttribute('data-nav-behind');
    }
  }

  syncChrome() {
    const depth = this.stack.length;
    const previous = Number(this.root.getAttribute('data-nav-depth')) || 0;
    this.root.setAttribute('data-nav-depth', String(depth));

    if (this.backButton) {
      if (depth > 0) {
        this.backButton.removeAttribute('hidden');
      } else {
        this.backButton.setAttribute('hidden', '');
      }
    }

    const current = depth > 0 ? this.stack[depth - 1] : null;
    const parent = depth > 1 ? this.stack[depth - 2] : null;

    this.setTitle(
      current ? current.node.getAttribute('data-nav-title') || 'Menu' : 'Menu',
      this.titleMotion(previous, depth)
    );
    if (this.backLabel) {
      const destination = parent ? parent.node.getAttribute('data-nav-title') || 'Menu' : 'Menu';
      this.backLabel.textContent = `Back to ${destination}`;
    }
  }

  onDocumentFocusIn(event) {
    if (!this.isDesktop) return;

    const panel = this.openPanel;
    if (!panel) return;

    const search = document.getElementById('predictive-search-container');
    if (search && search.contains(event.target)) {
      panel.hidePopover();
      return;
    }

    const header = this.closest('[data-section-type="header"]') || this;
    if (!header.contains(event.target) && !panel.contains(event.target)) {
      panel.hidePopover();
    }
  }

  onBreakpointChange() {
    this.closeAll();
    this.collapseFeatures();
    this.closeDrawer({ restoreFocus: false, immediate: true });
    this.resetStack();

    if (this.isDesktop) {
      this.root.querySelectorAll('[inert]').forEach((node) => node.removeAttribute('inert'));
      if (!this.root.hasAttribute('open')) this.root.setAttribute('open', '');
    }
  }

  onViewportChange() {
    if (this.isDesktop && this.openPanel) this.measureOffset();
  }

  measureOffset() {
    const header = this.closest('#SiteHeader') || this.root;
    const offset = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    this.root.style.setProperty('--header-nav-offset', offset + 'px');
    document.documentElement.style.setProperty('--header-nav-offset', offset + 'px');
  }

  closeAll() {
    this.panels().forEach((panel) => {
      if (panel.matches(':popover-open')) panel.hidePopover();
    });
  }

  collapseFeatures(scope) {
    (scope || this.root).querySelectorAll('[data-nav-feature][open]').forEach((feature) => {
      feature.open = false;
    });
  }

  // Theme Editor: block events bubble from the selected block, so binding on the
  // element scopes them to this navigation without a document listener.
  onBlockSelect(event) {
    if (!this.isDesktop) this.resetStack();

    const feature = event.target.closest('[data-nav-feature]');
    const item = event.target.closest('[data-header-nav-item]');
    if (!item) return;
    const panel = item.querySelector('[data-header-nav-panel]');
    window.cancelAnimationFrame(this.deselectFrame);

    if (this.isDesktop) {
      if (!panel) {
        this.closeAll();
        return;
      }
      if (!panel.matches(':popover-open')) panel.showPopover();
      if (feature) feature.open = true;
      else this.collapseFeatures(panel);
      return;
    }

    this.opener = document.querySelector('[data-header-nav-opener]');
    this.openDrawer();
    if (!panel) return;
    this.push(panel, null);

    const featurePanel = feature && feature.querySelector('[data-header-nav-feature-panel]');
    if (featurePanel) this.push(featurePanel, null);
  }

  // Deselect precedes the next select within the same frame; defer so a sibling
  // selection can cancel the close instead of reopening after it.
  onBlockDeselect() {
    window.cancelAnimationFrame(this.deselectFrame);
    this.deselectFrame = window.requestAnimationFrame(() => {
      this.closeAll();
      this.closeDrawer({ restoreFocus: false, immediate: true });
    });
  }
}

if (!customElements.get('header-navigation')) {
  customElements.define('header-navigation', HeaderNavigation);
}
